import React, { Dispatch, SetStateAction, useState } from 'react'
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  generateUtilityClasses,
} from '@mui/material'
import { styled } from '@mui/system'
import { useNavigate } from 'react-router-dom'
import { UspConfigurationUserRoleViewResult } from '../../../../../models'
import { storageGet, storageSet } from '../../../../../utils/storage'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { visuallyHidden } from '@mui/utils'
import { getComparator, stableSort } from '../../../../../utils/sorting'
import { appRoute } from '../../../../../constants'
import { usePopover } from '../../../../../components/custom-popover'
import CustomPopover from '../../../../../components/custom-popover/custom-popover'
import Iconify from '../../../../../components/iconify'
import Label from '../../../../../components/label/label'

const classes = generateUtilityClasses('Grid', ['root', 'activeItem', 'inactiveItem', 'loading', 'tableHead', 'tableCellName'])

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    margin: theme.spacing(3, 3, 3, 3),
  },
  [`& .${classes.activeItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.success.main,
    borderRadius: '4px',
  },
  [`& .${classes.inactiveItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.error.light,
    borderRadius: '4px',
  },
  [`& .${classes.tableHead}`]: {
    fontWeight: 600,
    background: theme.palette?.background?.main,
  },
  [`& .${classes.tableCellName}`]: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
  },
  [`& .${classes.loading}`]: {
    verticalAlign: 'top',
  },
}))

interface Props {
  searchUserRolesData?: any
  loading: boolean
  setDeleteConfirmDialog: Dispatch<SetStateAction<{ userRoleId: string } | undefined>>
}
const UserRolesTableBlock = ({ searchUserRolesData, loading, setDeleteConfirmDialog }: Props) => {
  const navigate = useNavigate()
  const initialOrder = (storageGet('user_roles_order') || 'asc') as 'asc' | 'desc'
  const initialOrderBy = (storageGet('user_roles_order_by') || 'UserRole') as keyof UspConfigurationUserRoleViewResult
  const [order, setOrder] = React.useState<'asc' | 'desc'>(initialOrder)
  const [orderBy, setOrderBy] = React.useState<keyof UspConfigurationUserRoleViewResult>(initialOrderBy)
  const [selected, setSelected] = useState<any>(null)
  const popover = usePopover()

  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(25)
  const paginatedData = searchUserRolesData

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const onRequestSort = (event: React.MouseEvent<unknown>, property: keyof UspConfigurationUserRoleViewResult) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    storageSet('user_roles_order', isAsc ? 'desc' : 'asc')
    storageSet('user_roles_order_by', property)
  }

  const createSortHandler = (property: keyof UspConfigurationUserRoleViewResult) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property)
  }

  return (
    <>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label='simple table' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'UserRole' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'UserRole'}
                  direction={orderBy === 'UserRole' ? order : 'asc'}
                  onClick={createSortHandler('UserRole')}
                >
                  Name
                  {orderBy === 'UserRole' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'SalesCount' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'SalesCount'}
                  direction={orderBy === 'SalesCount' ? order : 'asc'}
                  onClick={createSortHandler('SalesCount')}
                >
                  Sales Users
                  {orderBy === 'SalesCount' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'FinanceCount' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'FinanceCount'}
                  direction={orderBy === 'FinanceCount' ? order : 'asc'}
                  onClick={createSortHandler('FinanceCount')}
                >
                  Finance Users
                  {orderBy === 'FinanceCount' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'ServiceCount' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'ServiceCount'}
                  direction={orderBy === 'ServiceCount' ? order : 'asc'}
                  onClick={createSortHandler('ServiceCount')}
                >
                  Service Users
                  {orderBy === 'ServiceCount' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'IsActive' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsActive'}
                  direction={orderBy === 'IsActive' ? order : 'asc'}
                  onClick={createSortHandler('IsActive')}
                >
                  Status
                  {orderBy === 'IsActive' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} align={'right'}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stableSort(getComparator(order, orderBy), paginatedData as any[])
              ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              ?.map((item) => (
                <TableRow key={item?.ID} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell
                    className={classes.tableCellName}
                    onClick={() => navigate(appRoute?.SettingsAdminAddEditUserRoles.replace(':userRoleId', item?.ID?.toString() as string))}
                  >
                    {item.UserRole}
                  </TableCell>
                  <TableCell>{item?.SalesCount}</TableCell>
                  <TableCell>{item?.FinanceCount}</TableCell>
                  <TableCell>{item?.ServiceCount}</TableCell>
                  <TableCell>
                    <Label color={item.IsActive ? 'success' : 'error'}>{item.IsActive ? 'Active' : 'Inactive'}</Label>
                  </TableCell>
                  <TableCell align={'right'}>
                    <IconButton
                      onClick={(event: React.MouseEvent<HTMLElement>) => {
                        setSelected(item)
                        popover.onOpen(event)
                      }}
                    >
                      <Iconify icon='eva:more-vertical-fill' />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            {searchUserRolesData?.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align='center'>
                  No results
                </TableCell>
              </TableRow>
            )}
            {loading && (
              <TableRow>
                <TableCell colSpan={6} align='center'>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <CustomPopover open={popover.open} onClose={popover.onClose} arrow='top-right' sx={{ width: 140 }}>
          <MenuItem
            onClick={(e) => {
              navigate(appRoute?.SettingsAdminAddEditUserRoles.replace(':userRoleId', selected?.ID?.toString() as string))
              popover.onClose()
            }}
          >
            <Iconify icon='solar:pen-bold' />
            Edit
          </MenuItem>
          <MenuItem
            disabled={!selected?.IsActive}
            onClick={() => {
              setDeleteConfirmDialog({ userRoleId: selected.ID?.toString() as string })
              popover.onClose()
            }}
            sx={{ color: 'error.main' }}
          >
            <Iconify icon='solar:trash-bin-trash-bold' />
            Delete
          </MenuItem>
        </CustomPopover>
      </TableContainer>

      <Box sx={{ width: '100%', borderTop: '1px solid rgba(224, 224, 224, 1)' }}>
        <Paper>
          <TablePagination
            component='div'
            count={searchUserRolesData?.length as number}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>
    </>
  )
}

export default UserRolesTableBlock
